import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_BASE_URL } from '../config/config';
import { InvoiceClient } from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';

const apiClient = new InvoiceClient(API_BASE_URL, axiosInstance);

// Public: reached either straight after sign-up (embedded) or via an emailed pay-now link
// (standalone page). No token involved - the invoice id itself is the "auth".
export const useInvoicePaymentSummary = (invoiceId?: string) =>
  useQuery({
    queryKey: ['invoice-payment-summary', invoiceId],
    queryFn: async () => {
      const response = await apiClient.getInvoicePaymentSummary(
        invoiceId ?? '',
      );
      return response?.data ?? null;
    },
    enabled: !!invoiceId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

// Direct API search (no caching) - for admin tables with server-side paging
export const useInvoiceSearch = () => {
  const search = async (params: {
    page: number;
    pageSize: number;
    sortBy?: string;
    sortDescending?: boolean;
    filters?: any;
  }) => {
    const response = await apiClient.searchInvoices({
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy || 'CreatedUtc',
      sortDescending: params.sortDescending ?? true,
      filters: params.filters || {},
    });
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };

  return { search };
};

// Mutation hook - manually mark an invoice as paid (e.g. a payment received outside Stripe)
export const useMarkInvoiceAsPaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.markInvoiceAsPaid(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
};
